// Deleted sources, inside the Build panel (task 2026-10-05 "deleting a source never silently
// changes a built or hosted database"). GET /projects/{id}/source-deletions says what is shown:
//
//   design time: a study, dataset or file a version was built from is gone -> the notice says the
//     build is STALE and that the database and the stored .sql dumps still hold its rows. The
//     curator rebuilds (the Rebuild button above), drops the database (Drop database ->
//     ConfirmDialog, the name retyped -> POST .../dbdesign/database/drop) and/or purges the dumps
//     of a version (PurgeDialog: the request of the source's author recorded -> POST .../versions/{n}/purge-dump).
//   hosted: the rows stay with their lineage marked "source deleted"; a data admin removes them
//     only explicitly (RemoveRowsDialog: GET .../source-deletions/{id}/rows, the preview, then
//     POST .../remove-rows with the counts the preview showed; each row goes to the change log).
//   backups (task 2026-10-12): a portal admin only (can_purge_backups) purges a version's
//     pre-migration and dry-run dumps (PurgeBackupsDialog: the backup warning, the author's request,
//     the database name retyped -> POST .../versions/{n}/purge-backups).
//
// Nothing is rendered while the project has no recorded deletion, or when the call fails.
import { useEffect, useId, useRef, useState } from "react";
import { api } from "../../api";
import { Badge, Btn, ConfirmDialog, Spinner } from "../../components/Shared";
import { ErrorNote } from "../../components/Feedback";
import { useApi } from "../../useApi";

const when = t => (t ? new Date(t).toLocaleString() : "");
const small = { padding: "4px 10px", fontSize: 12 };
const label = { fontSize: 13, color: "#374151", display: "block", margin: "10px 0 4px" };
const input = { width: "100%", boxSizing: "border-box", padding: "8px 10px", border: "1px solid #d1d5db", borderRadius: 8, fontSize: 14, fontFamily: "inherit" };

function Dialog({ title, children, onClose }) {
  const ref = useRef(null);
  const id = useId();
  useEffect(() => {
    const onKey = e => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    ref.current?.focus();
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);
  return (
    <div onClick={e => { if (e.target === e.currentTarget) onClose(); }}
         style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.45)", zIndex: 220, display: "flex", alignItems: "center", justifyContent: "center", padding: 24 }}>
      <div ref={ref} role="dialog" aria-modal="true" aria-labelledby={`${id}-t`} tabIndex={-1}
           style={{ background: "#fff", borderRadius: 16, border: "1px solid #e5e7eb", maxWidth: 600, width: "100%", maxHeight: "90vh", overflowY: "auto", padding: "24px 28px", boxShadow: "0 8px 32px rgba(0,0,0,0.2)" }}>
        <h2 id={`${id}-t`} style={{ margin: "0 0 12px", fontSize: 18, fontWeight: 700, color: "#b91c1c" }}>{title}</h2>
        {children}
      </div>
    </div>
  );
}

// The purge needs the request of the deleted source's author (`requested_by`: their username,
// which the server checks against the deletions the version holds). Prefilled from the deletion.
function RequestedBy({ id, authors, value, onChange }) {
  return (
    <>
      <label htmlFor={`${id}-g`} style={label}>Requested by (the source's author)</label>
      <select id={`${id}-g`} value={value} onChange={e => onChange(e.target.value)} style={input}>
        {authors.map(a => <option key={a} value={a}>{a}</option>)}
      </select>
      <label htmlFor={`${id}-r`} style={label}>How the author asked (when, where)</label>
    </>
  );
}

export function PurgeDialog({ projectId, version, authors, onDone, onClose }) {
  const [author, setAuthor] = useState(authors[0] || "");
  const [request, setRequest] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const id = useId();
  const purge = async () => {
    setBusy(true);
    setError(null);
    try {
      await api.post(`/projects/${projectId}/dbdesign/versions/${version}/purge-dump`,
                     { requested_by: author, request });
      onDone();
    } catch (e) {
      setError(e);
      setBusy(false);
    }
  };
  return (
    <Dialog title={`Purge the dumps of version ${version}?`} onClose={onClose}>
      <p style={{ margin: 0, fontSize: 14, color: "#374151" }}>
        The stored .sql dump of version {version} still holds rows of a deleted
        source. Purging deletes them for good; the version is retired and cannot be downloaded or migrated to.
        It needs the request of the source's author, and is recorded in the audit log.
      </p>
      <RequestedBy id={id} authors={authors} value={author} onChange={setAuthor} />
      <textarea id={`${id}-r`} value={request} onChange={e => setRequest(e.target.value)} rows={3} style={input} />
      <ErrorNote error={error} />
      <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 16 }}>
        <Btn variant="secondary" onClick={onClose}>Cancel</Btn>
        <Btn variant="danger" disabled={busy || !author || !request.trim()} onClick={purge}>Purge dumps</Btn>
      </div>
    </Dialog>
  );
}

export function PurgeBackupsDialog({ projectId, version, authors, databaseName, onDone, onClose }) {
  const [author, setAuthor] = useState(authors[0] || "");
  const [request, setRequest] = useState("");
  const [typed, setTyped] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const id = useId();
  const purge = async () => {
    setBusy(true);
    setError(null);
    try {
      await api.post(`/projects/${projectId}/dbdesign/versions/${version}/purge-backups`,
                     { requested_by: author, request, confirm: typed.trim() });
      onDone();
    } catch (e) {
      setError(e);
      setBusy(false);
    }
  };
  return (
    <Dialog title={`Purge the backups of version ${version}?`} onClose={onClose}>
      <p style={{ margin: 0, fontSize: 14, color: "#374151" }}>
        The pre-migration and dry-run dumps of version {version} are backups of the hosted
        database {databaseName}: they hold every study's rows, not only the deleted source's. Purging deletes
        them for good. A pre-migration dump is the only copy of a column that migration dropped: afterwards,
        recovering that migration relies on the nightly dumps and point-in-time recovery alone.
      </p>
      <p style={{ margin: "8px 0 0", fontSize: 14, color: "#374151" }}>
        Only the version's migration script stays; the nightly dumps and the WAL archive are not touched.
        It needs the request of the source's author, and is recorded on the version and in the audit log.
      </p>
      <RequestedBy id={id} authors={authors} value={author} onChange={setAuthor} />
      <textarea id={`${id}-r`} value={request} onChange={e => setRequest(e.target.value)} rows={3} style={input} />
      <label htmlFor={`${id}-c`} style={label}>Type {databaseName} to confirm</label>
      <input id={`${id}-c`} value={typed} onChange={e => setTyped(e.target.value)} autoComplete="off"
             spellCheck={false} style={input} />
      <ErrorNote error={error} />
      <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 16 }}>
        <Btn variant="secondary" onClick={onClose}>Cancel</Btn>
        <Btn variant="danger" disabled={busy || !author || !request.trim() || typed.trim() !== databaseName}
             onClick={purge}>Purge backups</Btn>
      </div>
    </Dialog>
  );
}

export function RemoveRowsDialog({ projectId, deletion, onDone, onClose }) {
  const preview = useApi(`/projects/${projectId}/source-deletions/${deletion.id}/rows`);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const p = preview.data;
  const tables = (p && p.tables) || [];
  const refused = tables.filter(t => t.refused);
  const total = tables.reduce((a, t) => a + (t.rows || 0), 0);
  const remove = async () => {
    setBusy(true);
    setError(null);
    try {
      await api.post(`/projects/${projectId}/source-deletions/${deletion.id}/remove-rows`,
                     { expected: Object.fromEntries(tables.map(t => [t.table, t.rows])) });
      onDone();
    } catch (e) {
      setError(e);
      setBusy(false);
      preview.reload();                       // a stale preview: show the current counts
    }
  };
  return (
    <Dialog title={`Remove the rows of ${deletion.name}?`} onClose={onClose}>
      <p style={{ margin: 0, fontSize: 14, color: "#374151" }}>
        Only on the request of {deletion.author || "the source's author"}: these rows were loaded from the deleted
        {" "}{deletion.kind} and are deleted from the live database, each one recorded in the change log.
      </p>
      {preview.loading && <div style={{ marginTop: 12 }}><Spinner /> Counting the rows…</div>}
      <ErrorNote error={preview.error} />
      {p && (
        <>
          <ul aria-label="Rows to remove" style={{ margin: "12px 0 0", paddingLeft: 18, fontSize: 14 }}>
            {tables.map(t => (
              <li key={t.table}>
                <strong>{t.table}</strong>: {t.refused ? t.refused : `${t.rows} row${t.rows === 1 ? "" : "s"}`}
                {t.edited ? ` (${t.edited} edited since the build)` : ""}
              </li>
            ))}
          </ul>
          <p style={{ margin: "10px 0 0", fontSize: 13, color: "#6b7280" }}>{p.note}</p>
        </>
      )}
      <ErrorNote error={error} />
      <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 16 }}>
        <Btn variant="secondary" onClick={onClose}>Cancel</Btn>
        <Btn variant="danger" disabled={busy || !p || refused.length > 0 || tables.length === 0} onClick={remove}>
          Remove {total} row{total === 1 ? "" : "s"}
        </Btn>
      </div>
    </Dialog>
  );
}

export default function DeletedSourcesPanel({ projectId, canManage, refreshKey, onChange }) {
  const { data, reload } = useApi(`/projects/${projectId}/source-deletions`);
  const firstKey = useRef(refreshKey);
  useEffect(() => {                         // a build, a host or a migration changed the state
    if (refreshKey !== firstKey.current) {
      firstKey.current = refreshKey;
      reload();
    }
  }, [refreshKey, reload]);
  const [dropping, setDropping] = useState(false);
  const [purging, setPurging] = useState(null);       // {version, authors}
  const [purgingBackups, setPurgingBackups] = useState(null);   // {version, authors}
  const [removing, setRemoving] = useState(null);     // a deletion
  const [error, setError] = useState(null);
  if (!data || !(data.deletions || []).length) return null;
  const done = () => {
    setPurging(null);
    setPurgingBackups(null);
    setRemoving(null);
    reload();
    onChange && onChange();
  };
  const drop = async () => {
    setDropping(false);
    try {
      await api.post(`/projects/${projectId}/dbdesign/database/drop`, { confirm: data.database_name });
      done();
    } catch (e) {
      setError(e);
    }
  };
  const dumps = [...new Set(data.deletions.flatMap(d => d.dumps))]
    .filter(n => data.hosted || !(data.database_exists && n === data.loaded_version))
    .filter(n => !(data.hosted && n === data.loaded_version))
    .sort((a, b) => a - b);
  const backups = data.can_purge_backups
    ? [...new Set(data.deletions.flatMap(d => d.backups || []))].sort((a, b) => a - b) : [];
  const authorsOf = n => [...new Set(data.deletions.filter(d => d.versions.includes(n)).map(d => d.author).filter(Boolean))];
  // A version's backups also hold a source it was migrated FROM (the server's `backups` per deletion).
  const backupAuthorsOf = n => [...new Set(data.deletions.filter(d => (d.backups || []).includes(n))
    .map(d => d.author).filter(Boolean))];
  const tone = data.stale
    ? { background: "rgba(217,119,6,0.08)", border: "1px solid rgba(217,119,6,0.35)", color: "#92400e" }
    : { background: "#f9fafb", border: "1px solid #e5e7eb", color: "#374151" };
  return (
    <section data-testid="deleted-sources" aria-label="Deleted sources"
             style={{ marginTop: 14, padding: "10px 14px", borderRadius: 8, fontSize: 13, ...tone }}>
      <strong>{data.stale ? `${data.database_name} is stale` : "Deleted sources"}</strong>
      {data.notice && <p role={data.stale ? "alert" : undefined} style={{ margin: "6px 0 0" }}>{data.notice}</p>}
      <ul style={{ margin: "8px 0 0", paddingLeft: 18 }}>
        {data.deletions.map(d => (
          <li key={d.id} style={{ marginTop: 4 }}>
            {d.kind} <strong>{d.name}</strong>{d.author ? ` (by ${d.author})` : ""}, deleted by {d.deleted_by} {when(d.deleted_at)}
            {" "}· built into version{d.versions.length === 1 ? "" : "s"} {d.versions.join(", ")}
            {d.tables.length > 0 && <> · {d.tables.join(", ")}</>}
            {" "}{d.in_database && !data.hosted && <Badge color="amber">in the database</Badge>}
            {d.hosted && d.lineage_marked && <> <Badge color="gray">lineage marked</Badge></>}
            {d.rows_removed && <> <Badge color="green">rows removed by {d.rows_removed.by}</Badge></>}
            {d.purged_dumps.length > 0 && <> <Badge color="gray">dumps purged: v{d.purged_dumps.join(", v")}</Badge></>}
            {(d.purged_backups || []).length > 0 && (
              <> <Badge color="gray">backups purged: v{d.purged_backups.join(", v")}</Badge></>
            )}
            {d.removable && canManage && (
              <> <Btn variant="danger" style={small} onClick={() => setRemoving(d)}>Remove its rows…</Btn></>
            )}
          </li>
        ))}
      </ul>
      {canManage && (data.stale || dumps.length > 0) && (
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginTop: 10 }}>
          {data.stale && data.database_exists && !data.hosted && (
            <Btn variant="danger" style={small} onClick={() => setDropping(true)}>Drop database</Btn>
          )}
          {dumps.map(n => (
            <Btn key={n} variant="secondary" style={small} onClick={() => setPurging({ version: n, authors: authorsOf(n) })}>
              Purge dumps of v{n}…
            </Btn>
          ))}
        </div>
      )}
      {backups.length > 0 && (
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginTop: 10 }}>
          {backups.map(n => (
            <Btn key={n} variant="secondary" style={small}
                 onClick={() => setPurgingBackups({ version: n, authors: backupAuthorsOf(n) })}>
              Purge backups of v{n}…
            </Btn>
          ))}
        </div>
      )}
      <ErrorNote error={error} />
      {dropping && (
        <ConfirmDialog title={`Drop ${data.database_name}?`} confirmWord={data.database_name} confirmLabel="Drop"
                       onConfirm={drop} onClose={() => setDropping(false)}>
          <p style={{ margin: 0, fontSize: 14, color: "#374151" }}>
            The design-time database is dropped with every row in it, the deleted source's included. The
            versions and their .sql dumps stay; Build creates it again from the current design.
          </p>
        </ConfirmDialog>
      )}
      {purging && <PurgeDialog projectId={projectId} version={purging.version} authors={purging.authors}
                               onDone={done} onClose={() => setPurging(null)} />}
      {purgingBackups && <PurgeBackupsDialog projectId={projectId} version={purgingBackups.version}
                                             authors={purgingBackups.authors} databaseName={data.database_name}
                                             onDone={done} onClose={() => setPurgingBackups(null)} />}
      {removing && <RemoveRowsDialog projectId={projectId} deletion={removing} onDone={done}
                                     onClose={() => setRemoving(null)} />}
    </section>
  );
}
